import { demoFunc } from './dees-harness-permission-card.demo.js';
import {
  DeesElement,
  html,
  css,
  customElement,
  type TemplateResult,
  property,
  state,
  cssManager,
} from '@design.estate/dees-element';
import { themeDefaultStyles } from '../../00theme.js';
import type { IHarnessPermissionRequest, THarnessPermissionResponse } from '../interfaces.js';
import { harnessStringifyValue } from '../harness.helpers.js';
import '../../00group-button/dees-button/dees-button.js';
import '../../00group-feedback/dees-badge/dees-badge.js';
import '../../00group-utility/dees-icon/dees-icon.js';

declare global {
  interface HTMLElementTagNameMap {
    'dees-harness-permission-card': DeesHarnessPermissionCard;
  }
}

const badgeTypeForPermission = (permissionType: string): 'error' | 'warning' | 'default' => {
  if (permissionType === 'delete' || permissionType === 'shell') return 'error';
  if (permissionType === 'write' || permissionType === 'browser') return 'warning';
  return 'default';
};

/**
 * Permission prompt card: title, type badge, metadata key/values, and the
 * once / always / reject choices with an optional remember checkbox. Emits
 * `harness-permission-response` (bubbles, composed) and disables itself
 * once answered or while `busy`.
 */
@customElement('dees-harness-permission-card')
export class DeesHarnessPermissionCard extends DeesElement {
  public static demo = demoFunc;
  public static demoGroups = ['Agent Chat'];

  @property({ attribute: false })
  accessor request: IHarnessPermissionRequest = { id: '', title: '', type: 'permission' };

  @property({ type: Boolean })
  accessor busy: boolean = false;

  @state()
  accessor remember: boolean = false;

  @state()
  accessor answered: THarnessPermissionResponse | undefined = undefined;

  public static styles = [
    themeDefaultStyles,
    cssManager.defaultStyles,
    css`
      :host {
        display: block;
        font-family: var(--dees-font-family);
        /* entry: new boxes make space for themselves while fading in.
           A keyframes animation (not a transition) so the parent tree's
           universal theme-transition rule cannot override it in the shadow
           cascade; interpolate-size lets height animate from 0 to auto
           (Chromium 129+), elsewhere the height snaps and opacity still
           fades. */
        interpolate-size: allow-keywords;
        overflow: clip;
        animation: harness-entry var(--dees-transition-default) var(--dees-ease-out);
      }

      @keyframes harness-entry {
        from {
          height: 0;
          margin-top: 0;
          opacity: 0;
        }
      }

      @media (prefers-reduced-motion: reduce) {
        :host {
          animation: none;
        }
      }

      .card {
        padding: var(--dees-spacing-sm) var(--dees-spacing-md);
        border: 1px solid var(--dees-color-border-default);
        border-left: 2px solid var(--dees-color-accent-warning);
        border-radius: var(--dees-radius-lg);
        corner-shape: var(--dees-corner-shape);
        background: var(--dees-color-badge-warning-bg);
      }

      .titleRow {
        display: flex;
        align-items: center;
        gap: var(--dees-spacing-sm);
        margin-bottom: var(--dees-spacing-xs);
      }

      .titleRow dees-icon {
        color: var(--dees-color-accent-warning);
      }

      .title {
        flex: 1;
        min-width: 0;
        color: var(--dees-color-text-primary);
        font-size: var(--dees-font-control-size-sm, 12px);
        font-weight: 600;
      }

      .metaGrid {
        display: grid;
        grid-template-columns: auto minmax(0, 1fr);
        gap: 2px var(--dees-spacing-md);
        margin: var(--dees-spacing-xs) 0 var(--dees-spacing-sm);
        font-size: var(--dees-font-caption1-size, 11px);
      }

      .metaKey {
        color: var(--dees-color-text-secondary);
        white-space: nowrap;
      }

      .metaValue {
        color: var(--dees-color-text-primary);
        font-family: var(--dees-font-family-mono);
        overflow-wrap: anywhere;
      }

      .actions {
        display: flex;
        align-items: center;
        gap: var(--dees-spacing-sm);
        flex-wrap: wrap;
      }

      .rememberLabel {
        display: inline-flex;
        align-items: center;
        gap: var(--dees-spacing-xs);
        margin-left: auto;
        color: var(--dees-color-text-secondary);
        font-size: var(--dees-font-caption1-size, 11px);
        cursor: pointer;
        user-select: none;
      }

      .answeredNote {
        color: var(--dees-color-text-secondary);
        font-size: var(--dees-font-caption1-size, 11px);
      }
    `,
  ];

  public render(): TemplateResult {
    const request = this.request;
    const disabled = this.busy || this.answered !== undefined;
    const metadataEntries = Object.entries(request.metadata ?? {});
    return html`
      <div class="card">
        <div class="titleRow">
          <dees-icon icon="lucide:ShieldQuestionMark" iconSize="15"></dees-icon>
          <span class="title">${request.title || 'Permission requested'}</span>
          <dees-badge type=${badgeTypeForPermission(request.type)} .text=${request.type} rounded></dees-badge>
        </div>
        ${metadataEntries.length
          ? html`
              <div class="metaGrid">
                ${metadataEntries.map(([key, value]) => html`
                  <div class="metaKey">${key}</div>
                  <div class="metaValue">${harnessStringifyValue(value)}</div>
                `)}
              </div>
            `
          : ''}
        <div class="actions">
          ${this.answered
            ? html`<span class="answeredNote">Answered: ${this.answered}${this.remember ? ' (remembered)' : ''}</span>`
            : html`
                <dees-button type="accent" size="sm" ?disabled=${disabled} @clicked=${() => this.respond('once')}>
                  Allow Once
                </dees-button>
                <dees-button size="sm" ?disabled=${disabled} @clicked=${() => this.respond('always')}>
                  Always Allow
                </dees-button>
                <dees-button type="destructive" size="sm" ?disabled=${disabled} @clicked=${() => this.respond('reject')}>
                  Reject
                </dees-button>
                <label class="rememberLabel">
                  <input
                    type="checkbox"
                    .checked=${this.remember}
                    ?disabled=${disabled}
                    @change=${(event: Event) => {
                      this.remember = (event.currentTarget as HTMLInputElement).checked;
                    }}
                  />
                  remember for this session
                </label>
              `}
        </div>
      </div>
    `;
  }

  private respond(response: THarnessPermissionResponse): void {
    if (this.busy || this.answered !== undefined) return;
    this.answered = response;
    this.dispatchEvent(
      new CustomEvent('harness-permission-response', {
        detail: {
          requestId: this.request.id,
          response,
          remember: this.remember || response === 'always',
          request: this.request,
        },
        bubbles: true,
        composed: true,
      }),
    );
  }
}
